import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWebCheckInContext, WebCheckInProvider } from '../context/WebCheckInContext';
import { useBusiness4FlyoCheckInContext, Business4FlyoCheckInProvider } from '../context/Business4FlyoCheckInContext';
import { useExtPartnerCheckInContext, ExtPartnerCheckInProvider } from '../context/ExtPartnerCheckInContext';
import { useTripInfoContext } from '../context/TripInfoContext';
import { useAuth } from '../context/AuthContext';
import ShadcnTripInfoTable from '../components/ShadcnTripInfoTable';
import UnifiedFilterPanel from '../components/UnifiedFilterPanel';
import { webCheckInFilterConfig, business4FlyoFilterConfig, extPartnerFilterConfig } from '../config/filterConfigs';

import Business4FlyoCheckInTable from '../components/Business4FlyoCheckInTable';
import ExtPartnerCheckInTable from '../components/ExtPartnerCheckInTable';
import CheckInTable from '../components/tables/CheckInTable';
import StatusCards from '../components/ui/StatusCards';
import { normalizeStatus } from '../utils/webcheckinStatus';
import WebCheckinDashboardMetrics from '../components/WebCheckinDashboardMetrics';
import MainLayout from '../components/layout/MainLayout';

// Loaded on first open: it brings in the charts library.
const WebcheckinSlaDashboard = lazy(() => import('../components/WebcheckinSlaDashboard'));




/**
 * Web Check-in content component that uses the context
 * @param {Object} props - Component props
 * @param {string} props.activeTab - The active tab
 * @param {Function} props.setActiveTab - Function to set active tab
 * @returns {JSX.Element} WebCheckInContent component
 */
const WebCheckInContent = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const {
    checkIns,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    loadCheckIns,
    pagination,
    setPagination,
    changePage,
    filters,
    updateFilters,
    searchWithFilters,
    clearFilters,
    getSortedData
  } = useWebCheckInContext();

  // Business4Flyo Check-in context
  const {
    checkIns: business4FlyoCheckIns,
    loading: business4FlyoLoading,
    error: business4FlyoError,
    statusFilter: business4FlyoStatusFilter,
    setStatusFilter: setBusiness4FlyoStatusFilter,
    loadCheckIns: loadBusiness4FlyoCheckIns,
    pagination: business4FlyoPagination,
    changePage: changeBusiness4FlyoPage,
    filters: business4FlyoFilters,
    updateFilters: updateBusiness4FlyoFilters,
    searchWithFilters: searchBusiness4FlyoWithFilters,
    clearFilters: clearBusiness4FlyoFilters
  } = useBusiness4FlyoCheckInContext();

  const {
    checkIns: extPartnerCheckIns,
    loading: extPartnerLoading,
    error: extPartnerError,
    statusFilter: extPartnerStatusFilter,
    setStatusFilter: setExtPartnerStatusFilter,
    loadCheckIns: loadExtPartnerCheckIns,
    pagination: extPartnerPagination,
    changePage: changeExtPartnerPage,
    filters: extPartnerFilters,
    updateFilters: updateExtPartnerFilters,
    searchWithFilters: searchExtPartnerWithFilters,
    clearFilters: clearExtPartnerFilters
  } = useExtPartnerCheckInContext();

  // Trip Info context for reloading when the tab is activated
  const {
    loadTripData,
    pagination: tripPagination
  } = useTripInfoContext();

  const isB2bUser = activeTab === 'b2bWebCheckIn';
  
  // Track last loaded tab to prevent duplicate calls
  const lastLoadedTabRef = useRef(null);

  // Load data whenever switching between webCheckIn and b2bWebCheckIn
  useEffect(() => {
    if (activeTab === 'webCheckIn' || activeTab === 'b2bWebCheckIn') {
      // Only load if this is a different tab than last time
      if (lastLoadedTabRef.current !== activeTab) {
        lastLoadedTabRef.current = activeTab;
        const customFilters = isB2bUser ? { isB2bUser: true } : {};
        loadCheckIns(0, pagination.itemsPerPage, customFilters);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isB2bUser]); // Removed loadCheckIns and pagination.itemsPerPage to prevent re-runs

  // Load Business4Flyo check-ins when tab is activated
  useEffect(() => {
    if (activeTab === 'business4FlyoWebCheckIn') {
      loadBusiness4FlyoCheckIns(1, business4FlyoPagination.itemsPerPage);
    }
  }, [activeTab, loadBusiness4FlyoCheckIns, business4FlyoPagination.itemsPerPage]);

  useEffect(() => {
    if (activeTab === 'extPartnerCheckin') {
      loadExtPartnerCheckIns(0, extPartnerPagination.itemsPerPage, {}, { force: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Ensure Trip Info tab reloads latest dataset when activated
  useEffect(() => {
    if (activeTab === 'tripInfo') {
      loadTripData(tripPagination.pageNumber, tripPagination.itemsPerPage);
    }
  }, [activeTab, loadTripData, tripPagination.pageNumber, tripPagination.itemsPerPage]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');



  // Note: Date filtering is now handled through the filters context
  // No need for separate local state for date ranges

  // Data source filter state
  const [dataSourceFilter, setDataSourceFilter] = useState([]);

  // Memoized callback for data source filter changes
  const handleDataSourceFilterChange = useCallback((newSources) => {
    setDataSourceFilter(newSources);
  }, []);

  // Verify authentication on component mount - data loading is handled by useWebCheckIns hook
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('Unauthorized access attempt. Redirecting to login...');
      navigate('/login', { replace: true });
    }
    // Note: Data loading is handled by the useWebCheckIns hook, no need to duplicate here
  }, [isAuthenticated, navigate]);

  // Handle check-in row click
  const handleCheckInClick = (checkIn) => {
    // FIXED: Use the correct property name (ticket_id) when navigating
    console.log('Navigating to check-in details with ticket ID:', checkIn.ticket_id);
    console.log('userid is: ',checkIn.user_id)
    // Include the current active tab as a query parameter
    navigate(`/check-in/${checkIn.ticket_id}/${checkIn.user_id}?source=${activeTab}`);
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Date range handling is now managed through the filters context
  // No need for separate handlers

  // Reset all filters
  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDataSourceFilter([]);
    setSortBy('');
    setSortDirection('asc');
    // Use the context's clearFilters to reset date filters and other API filters
    clearFilters();
  };

  // Refresh data - reload based on active tab
  const handleRefresh = () => {
    if (activeTab === 'tripInfo') {
      loadTripData(tripPagination.pageNumber, tripPagination.itemsPerPage);
    } else if (activeTab === 'business4FlyoWebCheckIn') {
      loadBusiness4FlyoCheckIns(1, business4FlyoPagination.itemsPerPage, {}, { force: true });
    } else if (activeTab === 'extPartnerCheckin') {
      loadExtPartnerCheckIns(0, extPartnerPagination.itemsPerPage, {}, { force: true });
    } else {
      const customFilters = isB2bUser ? { isB2bUser: true } : {};
      loadCheckIns(0, pagination.itemsPerPage, customFilters);
    }
  };

  // Status cards pass 'all' or a status code; the APIs take the code ('' for all).
  const statusParam = (status) => (status === 'all' ? '' : status);



  // Format date and time separately without timezone conversion - used for table columns
  // eslint-disable-next-line no-unused-vars
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: 'N/A' };
    try {
      // Parse date and time parts separately to avoid timezone conversion
      const [datePart, timePart] = dateString.split('T');
      const timeOnly = timePart ? timePart.replace('Z', '').split('.')[0] : '00:00:00'; // Remove Z and milliseconds
      const [hours, minutes] = timeOnly.split(':');

      // Format date as DD/MM/YYYY
      const [year, month, day] = datePart.split('-');
      const formattedDate = `${day}/${month}/${year}`;

      // Format time as HH:MM AM/PM
      const hour24 = parseInt(hours);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      const formattedTime = `${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;

      return {
        date: formattedDate,
        time: formattedTime
      };
    } catch (error) {
      console.error('Error formatting date/time:', error);
      return { date: 'Invalid date', time: 'Invalid time' };
    }
  };



  // Get the appropriate data array based on sorting
  const getDataForSorting = () => {
    // Always use departure date sorted data as the primary sort (ascending order)
    // This ensures departure date is always the default sort regardless of other filters
    if (getSortedData) {
      return getSortedData('departureDate', 'asc');
    }
    // Fallback to regular checkIns data if sortedData not available
    return checkIns;
  };

  // Filter and sort check-ins
  const filteredCheckIns = getDataForSorting()
    .filter(checkIn => {
      const matchesSearch = searchQuery === '' ||
        checkIn.id.toString().includes(searchQuery.toLowerCase()) ||
        (checkIn.pnr && checkIn.pnr.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (checkIn.flightNumber && checkIn.flightNumber.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesStatus = statusFilter === 'all' || normalizeStatus(checkIn.status) === statusFilter;

      // Filter by PNR from filter panel
      const matchesPnr = !filters.pnr ||
        (checkIn.pnr && checkIn.pnr.toLowerCase().includes(filters.pnr.toLowerCase()));

      // Filter by data source (if available in the future)
      const matchesDataSource = dataSourceFilter.length === 0 ||
        (checkIn.ticketSource && dataSourceFilter.includes(checkIn.ticketSource));

      // Filter by departure date range using filters from context
      const departureDate = new Date(checkIn.departureDate);
      const departureStartDate = filters.departureDateMin ? new Date(filters.departureDateMin) : null;
      const departureEndDate = filters.departureDateMax ? new Date(filters.departureDateMax) : null;

      // For date comparison, we need to compare just the date parts (not time)
      const departureDateOnly = new Date(departureDate.getFullYear(), departureDate.getMonth(), departureDate.getDate());
      const startDateOnly = departureStartDate ? new Date(departureStartDate.getFullYear(), departureStartDate.getMonth(), departureStartDate.getDate()) : null;
      const endDateOnly = departureEndDate ? new Date(departureEndDate.getFullYear(), departureEndDate.getMonth(), departureEndDate.getDate()) : null;

      const matchesDepartureDate = (!startDateOnly || departureDateOnly >= startDateOnly) &&
                                  (!endDateOnly || departureDateOnly <= endDateOnly);

      // Filter by scheduled time range using filters from context
      const scheduledDate = checkIn.scheduledTime ? new Date(checkIn.scheduledTime) : null;
      const scheduledStartDate = filters.scheduledAtMin ? new Date(filters.scheduledAtMin) : null;
      const scheduledEndDate = filters.scheduledAtMax ? new Date(filters.scheduledAtMax) : null;

      // For scheduled date comparison, also compare just the date parts
      const scheduledDateOnly = scheduledDate ? new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate()) : null;
      const scheduledStartDateOnly = scheduledStartDate ? new Date(scheduledStartDate.getFullYear(), scheduledStartDate.getMonth(), scheduledStartDate.getDate()) : null;
      const scheduledEndDateOnly = scheduledEndDate ? new Date(scheduledEndDate.getFullYear(), scheduledEndDate.getMonth(), scheduledEndDate.getDate()) : null;

      const matchesScheduledDate = (!scheduledStartDateOnly || !scheduledDateOnly || scheduledDateOnly >= scheduledStartDateOnly) &&
                                  (!scheduledEndDateOnly || !scheduledDateOnly || scheduledDateOnly <= scheduledEndDateOnly);

      return matchesSearch && matchesStatus && matchesPnr && matchesDataSource && matchesDepartureDate && matchesScheduledDate;
    })
    .sort((a, b) => {
      // Primary sort: Always sort by departure date in ascending order
      const departureDateA = new Date(a.departureDate);
      const departureDateB = new Date(b.departureDate);
      const departureDateComparison = departureDateA - departureDateB;

      // If departure dates are different, use that as the primary sort
      if (departureDateComparison !== 0) {
        return departureDateComparison;
      }

      // Secondary sort: Apply user-selected sorting if departure dates are the same
      if (sortBy && sortBy !== 'departureDate') {
        const direction = sortDirection === 'asc' ? 1 : -1;
        switch (sortBy) {
          case 'id':
            return direction * (a.id - b.id);
          case 'user_id':
            return direction * (a.user_id - b.user_id);
          case 'mobileNumber':
            return direction * (a.mobileNumber || '').localeCompare(b.mobileNumber || '');
          case 'pnr':
            return direction * (a.pnr || '').localeCompare(b.pnr || '');
          case 'flightNumber':
            return direction * (a.flightNumber || '').localeCompare(b.flightNumber || '');
          case 'origin':
            return direction * (a.origin || '').localeCompare(b.origin || '');
          case 'destination':
            return direction * (a.destination || '').localeCompare(b.destination || '');
          case 'status':
            return direction * (a.status || '').localeCompare(b.status || '');
          case 'scheduledTime':
            const scheduledA = new Date(a.scheduledTime || 0);
            const scheduledB = new Date(b.scheduledTime || 0);
            return direction * (scheduledA - scheduledB);
          default:
            return 0;
        }
      }

      // If no secondary sort or same departure dates, maintain original order
      return 0;
    });

  // Updated logout function that uses the auth context
  // eslint-disable-next-line no-unused-vars
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
      // Optional: For demo purposes
      setTimeout(() => {
        alert("Logged out successfully");
      }, 100);
    } catch (error) {
      console.error('Error during logout:', error);
      alert("Logout failed. Please try again.");
    }
  };

  const isWebCheckInTab = activeTab === 'webCheckIn' || activeTab === 'b2bWebCheckIn';
  const isBusiness4FlyoTab = activeTab === 'business4FlyoWebCheckIn';
  const isExtPartnerTab = activeTab === 'extPartnerCheckin';
  const isTripInfoTab = activeTab === 'tripInfo';

  return (
    <MainLayout activeTab={activeTab} setActiveTab={setActiveTab}>
      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden'
      }}>
        {/* Status Cards - Show for both Web Check-in tabs */}
        {isWebCheckInTab && (
          <div className="px-6 pt-4 pb-2">
            <StatusCards
              checkIns={checkIns}
              statusFilter={statusFilter}
              onStatusClick={(status) => {
                setStatusFilter(status);
                // Also update the API filter for consistency
                updateFilters({ checkinStatus: statusParam(status) });
              }}
              loading={loading}
            />
          </div>
        )}

        {/* Status Cards - Show for Business4Flyo Web Check-in tab */}
        {isBusiness4FlyoTab && (
          <div className="px-6 pt-4 pb-2">
            <StatusCards
              checkIns={business4FlyoCheckIns}
              statusFilter={business4FlyoStatusFilter}
              onStatusClick={(status) => {
                setBusiness4FlyoStatusFilter(status);
                const statusFilters = { checkinStatuses: statusParam(status) };
                updateBusiness4FlyoFilters(statusFilters);
                loadBusiness4FlyoCheckIns(
                  1,
                  business4FlyoPagination.itemsPerPage,
                  statusFilters,
                  { force: true }
                );
              }}
              loading={business4FlyoLoading}
            />
          </div>
        )}

        {isExtPartnerTab && (
          <div className="px-6 pt-4 pb-2">
            <StatusCards
              checkIns={extPartnerCheckIns}
              statusFilter={extPartnerStatusFilter}
              onStatusClick={(status) => {
                setExtPartnerStatusFilter(status);
                const statusFilters = { checkinStatus: statusParam(status) };
                updateExtPartnerFilters(statusFilters);
                loadExtPartnerCheckIns(
                  0,
                  extPartnerPagination.itemsPerPage,
                  statusFilters,
                  { force: true }
                );
              }}
              loading={extPartnerLoading}
            />
          </div>
        )}

        {/* Filter Panel - Show for both Web Check-in tabs */}
        {isWebCheckInTab && (
          <div className="sticky top-0 z-50 px-6 mb-6">
            <UnifiedFilterPanel
              config={webCheckInFilterConfig}
              filters={filters}
              onFiltersChange={updateFilters}
              onSearch={searchWithFilters}
              onReset={clearFilters}
              onRefresh={handleRefresh}
              loading={loading}
            />
          </div>
        )}

        {/* Filter Panel - Show for Business4Flyo Web Check-in tab */}
        {isBusiness4FlyoTab && (
          <div className="sticky top-0 z-50 px-6 mb-6">
            <UnifiedFilterPanel
              config={business4FlyoFilterConfig}
              filters={business4FlyoFilters}
              onFiltersChange={updateBusiness4FlyoFilters}
              onSearch={searchBusiness4FlyoWithFilters}
              onReset={() => {
                setBusiness4FlyoStatusFilter('all');
                clearBusiness4FlyoFilters();
              }}
              onRefresh={handleRefresh}
              loading={business4FlyoLoading}
            />
          </div>
        )}

        {isExtPartnerTab && (
          <div className="sticky top-0 z-50 px-6 mb-6">
            <UnifiedFilterPanel
              config={extPartnerFilterConfig}
              filters={extPartnerFilters}
              onFiltersChange={updateExtPartnerFilters}
              onSearch={searchExtPartnerWithFilters}
              onReset={() => {
                setExtPartnerStatusFilter('all');
                clearExtPartnerFilters();
              }}
              onRefresh={handleRefresh}
              loading={extPartnerLoading}
            />
          </div>
        )}

        {/* Scrollable content area */}
        <div style={{
          flex: 1,
          overflowY: isTripInfoTab ? 'hidden' : 'auto',
          padding: '0 16px 16px 16px',
          height: isTripInfoTab ? 'calc(100vh - 100px)' : 'auto'
        }}>
          {(isWebCheckInTab || isBusiness4FlyoTab || isExtPartnerTab) ? (
            isBusiness4FlyoTab ? (
              <Business4FlyoCheckInTable
                checkIns={business4FlyoCheckIns}
                loading={business4FlyoLoading}
                error={business4FlyoError}
                pagination={business4FlyoPagination}
                onPageChange={changeBusiness4FlyoPage}
                onRowsPerPageChange={(newItemsPerPage) => {
                  // Reload with new items per page
                  loadBusiness4FlyoCheckIns(1, newItemsPerPage);
                }}
              />
            ) : isExtPartnerTab ? (
              <ExtPartnerCheckInTable
                checkIns={extPartnerCheckIns}
                loading={extPartnerLoading}
                error={extPartnerError}
                pagination={extPartnerPagination}
                onPageChange={changeExtPartnerPage}
                onRowsPerPageChange={(newItemsPerPage) => {
                  loadExtPartnerCheckIns(0, newItemsPerPage, {}, { force: true });
                }}
              />
            ) : (
              <div className="px-6 h-full overflow-hidden flex flex-col">
                <CheckInTable
                  checkIns={filteredCheckIns}
                  loading={loading}
                  error={error}
                  onRowClick={handleCheckInClick}
                  pagination={pagination}
                  onPageChange={changePage}
                  onRowsPerPageChange={(newItemsPerPage) => {
                    if (setPagination) {
                      setPagination(prev => ({
                        ...prev,
                        itemsPerPage: newItemsPerPage,
                        pageNumber: 1
                      }));
                    }
                  }}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              </div>
            )
          ) : activeTab === 'webCheckinDashboard' ? (
            <WebCheckinDashboardMetrics />
          ) : activeTab === 'webcheckinSla' ? (
            <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading...</div>}>
              <WebcheckinSlaDashboard />
            </Suspense>
          ) : (
            <div style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <ShadcnTripInfoTable
                externalDataSourceFilter={dataSourceFilter}
                onDataSourceFilterChange={handleDataSourceFilterChange}
                externalSearchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                onReset={handleReset}
                onRefresh={handleRefresh}
                loading={loading}
              />
            </div>
          )}
        </div>
      </div>


    </MainLayout>
  );
};

/**
 * Dashboard page component that provides the appropriate context based on active tab
 * @returns {JSX.Element} DashboardPage component
 */
const DashboardPage = () => {
  const location = useLocation();
  // Set active tab from location state if available, otherwise land on the SLA tab
  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab || 'webcheckinSla'
  );

  // Use a single provider with fixed B2B mode to prevent recreation
  // The B2B mode will be handled dynamically within the content component
  return (
    <WebCheckInProvider isB2bUser={activeTab === 'b2bWebCheckIn'}>
      <Business4FlyoCheckInProvider>
        <ExtPartnerCheckInProvider>
          <WebCheckInContent activeTab={activeTab} setActiveTab={setActiveTab} />
        </ExtPartnerCheckInProvider>
      </Business4FlyoCheckInProvider>
    </WebCheckInProvider>
  );
};

export default DashboardPage;
